'use client';

import { c as _c } from "react/compiler-runtime";
import { truncateUtf8Text, utf8ByteLength } from '@morfi/contract-sdk';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { MarketImageDraft } from '@/components/images/market-picker';
import { MarketImagePickerModal } from '@/components/images/market-picker-modal';
import { OutcomeImageField } from '@/components/launchpad/outcome-image-field';
import { AppTooltip } from '@/components/ui/app-tooltip';
import type { FieldShellRenderArgs } from '@/components/ui/field-shell';
import { FieldShell } from '@/components/ui/field-shell';
import { Icon } from '@/components/ui/icon';
import { Copy01Icon, Delete02Icon } from '@/components/ui/icon/glyphs';
import { PROPOSAL_FIELDS } from '@/config/proposal-fields';
import { useImageSearchPrefetch } from '@/hooks/launchpad/use-image-search-prefetch';
import { useNsfwStatusByUrl } from '@/hooks/use-nsfw-status-by-url';
import { buildOutcomeSearchQuery } from '@/lib/launchpad/outcome-search-query';
import { useUppercaseTickerInput } from '@/lib/launchpad/uppercase-ticker-input';
import { cn } from '@/lib/ui/utils';
interface OutcomeInputProps {
  index: number;
  label: string;
  ticker: string;
  /** Market ticker entered on step 1 — used as the live prefix for this outcome's ticker. */
  marketTicker: string;
  /** Market title entered on step 1 — passed to the image generator for prompt context. */
  marketTitle: string;
  imageUrl: string | null;
  onLabelChange: (label: string) => void;
  onTickerChange: (ticker: string) => void;
  /**
   * Deferred image lifecycle — `onImagePending` fires with the cropped local
   * File draft the moment the user confirms, and final upload happens at
   * create-time in compact market image order.
   */
  onImagePending: (draft: MarketImageDraft) => void;
  onImageReject: (error: Error) => void;
  /** Clear the slot — fires from the inline X button. */
  onImageRemove: () => void;
  onRemove: () => void;
  canRemove: boolean;
  error?: string | null;
  tickerError?: string | null;
  /** Marks the image as required and renders a persistent visible label. */
  imageRequired?: boolean;
  /** Visible image validation error connected to the upload control. */
  imageError?: string | null;
  /** AI generate flow: this outcome's label has arrived but its image fetch
   *  hasn't settled yet (no `outcome.locked` / `outcome.failed` event). When
   *  true and `imageUrl` is null, the slot renders a Skeleton instead of the
   *  empty dashed-upload affordance — that affordance would otherwise mislead
   *  the user into thinking they need to upload an image manually. */
  imageLoading?: boolean;
  /** Optional action(s) rendered inside the card, below the inputs. Used by
   *  the manual flow to surface the per-row "Generate image" CTA inside the
   *  same bordered container as the label/ticker inputs and image thumb. */
  footerSlot?: ReactNode;
  /** Perceived-speed opt-in for the AI generate flow: the outcome image is
   *  actively awaited in-view as it streams in, so load it eagerly at high
   *  priority (skip the lazy viewport gate). Default false — the manual flow
   *  keeps the standard lazy path. */
  eagerImage?: boolean;
  /** AI generate flow: inline preview data-URI for this outcome — the bytes
   *  the client DISPLAYS (zero network, never the gateway). Preferred over
   *  `imageUrl` for rendering; `imageUrl` (durable IPFS) is for submission. */
  imagePreview?: string;
  /** AI generate flow: the durable pin permanently failed — preview still
   *  displays, but the slot has no submittable URL, so flag it. */
  pinFailed?: boolean;
  /** Manual image-picker optimization. Disabled in AI generation because
   *  generated images are produced by the Lambda worker, not the picker search. */
  prefetchImageSearch?: boolean;
}
const STATIC_MAX_TICKER_LENGTH: number = PROPOSAL_FIELDS.outcomeTicker.maxLength;
const COMBINED_TICKER_MAX_LENGTH: number = PROPOSAL_FIELDS.tickerCombined.maxLength;

/**
 * Outcome ticker max shrinks as the market ticker grows so that the combined
 * ticker stays within the configured UTF-8 byte budget. The literal `X`
 * separator counts toward the budget — hence the `- 1` below.
 */
function computeOutcomeTickerMax(marketTickerLength: number): number {
  const remaining = COMBINED_TICKER_MAX_LENGTH - 1 - marketTickerLength;
  return Math.max(0, Math.min(STATIC_MAX_TICKER_LENGTH, remaining));
}
export function OutcomeInput(t0) {
  const $ = _c(119);
  const {
    index,
    label,
    ticker,
    marketTicker,
    marketTitle,
    imageUrl,
    onLabelChange,
    onTickerChange,
    onImagePending,
    onImageReject,
    onImageRemove,
    onRemove,
    canRemove,
    error,
    tickerError,
    imageRequired: t1,
    imageError,
    imageLoading: t2,
    footerSlot,
    eagerImage: t3,
    imagePreview,
    pinFailed: t4,
    prefetchImageSearch: t5
  } = t0;
  const imageRequired = t1 === undefined ? false : t1;
  const imageLoading = t2 === undefined ? false : t2;
  const eagerImage = t3 === undefined ? false : t3;
  const pinFailed = t4 === undefined ? false : t4;
  const prefetchImageSearch = t5 === undefined ? true : t5;
  const t = useTranslations("launchpad.form");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const tickerInput = useUppercaseTickerInput(onTickerChange);
  const isBlobUrl = imageUrl?.startsWith("blob:") ?? false;
  const nsfwLookup = useNsfwStatusByUrl(isBlobUrl ? null : imageUrl);
  const displaySrc = imagePreview !== undefined && imagePreview.length > 0 ? imagePreview : imageUrl;
  let t6;
  if ($[0] !== label || $[1] !== marketTitle) {
    t6 = buildOutcomeSearchQuery(marketTitle, label);
    $[0] = label;
    $[1] = marketTitle;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const t7 = prefetchImageSearch && !imageModalOpen;
  let t8;
  if ($[3] !== t7) {
    t8 = {
      enabled: t7
    };
    $[3] = t7;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  useImageSearchPrefetch(t6, t8);
  let t9;
  if ($[5] !== marketTicker) {
    t9 = marketTicker.trim();
    $[5] = marketTicker;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const hasMarket = t9.length > 0;
  const tickerPrefix = hasMarket ? `$${marketTicker}` : "$";
  let T0;
  let T1;
  let canApplyDerivedTicker;
  let dynamicMaxTickerLength;
  let handleCopyLabelToTicker;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let tickerBytes;
  if ($[7] !== canRemove || $[8] !== displaySrc || $[9] !== eagerImage || $[10] !== error || $[11] !== imageError || $[12] !== imageLoading || $[13] !== imageRequired || $[14] !== index || $[15] !== label || $[16] !== marketTicker || $[17] !== nsfwLookup.isNsfw || $[18] !== onImageRemove || $[19] !== onLabelChange || $[20] !== onRemove || $[21] !== onTickerChange || $[22] !== pinFailed || $[23] !== t || $[24] !== ticker || $[25] !== tickerError) {
    dynamicMaxTickerLength = computeOutcomeTickerMax(utf8ByteLength(marketTicker));
    tickerBytes = utf8ByteLength(ticker);
    const derivedTickerFromLabel = truncateUtf8Text(label.replace(/\p{White_Space}+/gu, "").toUpperCase(), dynamicMaxTickerLength);
    canApplyDerivedTicker = derivedTickerFromLabel.length > 0 && ticker.length === 0;
    let t28;
    if ($[50] !== onLabelChange) {
      t28 = function handleLabelChange(event) {
        onLabelChange(event.target.value);
      };
      $[50] = onLabelChange;
      $[51] = t28;
    } else {
      t28 = $[51];
    }
    const handleLabelChange = t28;
    handleCopyLabelToTicker = function handleCopyLabelToTicker() {
      if (!canApplyDerivedTicker) {
        return;
      }
      onTickerChange(derivedTickerFromLabel);
    };
    let t29;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = function handleOpenImageModal() {
        setImageModalOpen(true);
      };
      $[52] = t29;
    } else {
      t29 = $[52];
    }
    const handleOpenImageModal = t29;
    const tickerId = `outcome-ticker-${String(index)}`;
    t27 = "flex flex-col gap-3 rounded-control border border-border-primary bg-background-primary p-3";
    T1 = OutcomeImageField;
    t17 = index;
    t18 = displaySrc;
    t19 = nsfwLookup.isNsfw;
    t20 = imageRequired;
    t21 = imageError;
    t22 = imageLoading;
    t23 = eagerImage;
    t24 = pinFailed;
    t25 = handleOpenImageModal;
    t26 = onImageRemove;
    t15 = "flex min-w-0 flex-1 flex-col gap-3";
    const t30 = `outcome-${String(index)}`;
    const t31 = index + 1;
    let t32;
    if ($[53] !== t || $[54] !== t31) {
      t32 = t("outcomeLabel", {
        number: t31
      });
      $[53] = t;
      $[54] = t31;
      $[55] = t32;
    } else {
      t32 = $[55];
    }
    const t33 = error ?? undefined;
    let t34;
    if ($[56] !== canRemove || $[57] !== handleLabelChange || $[58] !== label || $[59] !== onRemove || $[60] !== t) {
      t34 = t35 => {
        const {
          controlProps
        } = t35;
        return <div className={cn("flex h-10 w-full min-w-0 items-center rounded-control border border-border-primary bg-background-primary shadow-none", "transition-[color,background-color,border-color,box-shadow]", "focus-within:ring-2 focus-within:ring-border-primary focus-within:ring-offset-2", "hover:border-border-brand", controlProps["aria-invalid"] === true && "border-feedback-error-border focus-within:ring-feedback-error-border")}><input {...controlProps} type="text" value={label} onChange={handleLabelChange} placeholder={t("outcomePlaceholder")} className="min-w-0 flex-1 bg-transparent px-3 py-2 text-body-lg text-foreground-primary outline-none placeholder:text-foreground-tertiary focus-visible:outline-none md:text-body" />{canRemove ? <button type="button" onClick={onRemove} aria-label={t("removeOutcome")} className="flex h-full shrink-0 items-center justify-center rounded-r-[calc(var(--radius-control)-1px)] border-l border-border-primary bg-background-tertiary/40 px-3 text-foreground-secondary transition-colors hover:bg-background-tertiary hover:text-feedback-error-content focus-visible:outline-none"><Icon icon={Delete02Icon} className="size-4" /></button> : <AppTooltip content={t("removeOutcomeDisabledTooltip")}><span className="inline-flex h-full"><button type="button" disabled={true} aria-label={t("removeOutcome")} className="flex h-full shrink-0 items-center justify-center rounded-r-[calc(var(--radius-control)-1px)] border-l border-border-primary bg-background-tertiary/40 px-3 text-foreground-tertiary"><Icon icon={Delete02Icon} className="size-4" /></button></span></AppTooltip>}</div>;
      };
      $[56] = canRemove;
      $[57] = handleLabelChange;
      $[58] = label;
      $[59] = onRemove;
      $[60] = t;
      $[61] = t34;
    } else {
      t34 = $[61];
    }
    if ($[62] !== t30 || $[63] !== t32 || $[64] !== t33 || $[65] !== t34) {
      t16 = <FieldShell id={t30} label={t32} error={t33} labelClassName="sr-only">{t34}</FieldShell>;
      $[62] = t30;
      $[63] = t32;
      $[64] = t33;
      $[65] = t34;
      $[66] = t16;
    } else {
      t16 = $[66];
    }
    T0 = FieldShell;
    t10 = tickerId;
    if ($[67] !== t) {
      t11 = t("outcomeTickerLabel");
      $[67] = t;
      $[68] = t11;
    } else {
      t11 = $[68];
    }
    t12 = tickerError ?? undefined;
    t13 = "sr-only";
    t14 = t("outcomeTickerByteCount", {
      count: tickerBytes,
      max: dynamicMaxTickerLength
    });
    $[7] = canRemove;
    $[8] = displaySrc;
    $[9] = eagerImage;
    $[10] = error;
    $[11] = imageError;
    $[12] = imageLoading;
    $[13] = imageRequired;
    $[14] = index;
    $[15] = label;
    $[16] = marketTicker;
    $[17] = nsfwLookup.isNsfw;
    $[18] = onImageRemove;
    $[19] = onLabelChange;
    $[20] = onRemove;
    $[21] = onTickerChange;
    $[22] = pinFailed;
    $[23] = t;
    $[24] = ticker;
    $[25] = tickerError;
    $[26] = T0;
    $[27] = T1;
    $[28] = canApplyDerivedTicker;
    $[29] = dynamicMaxTickerLength;
    $[30] = handleCopyLabelToTicker;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
    $[44] = t23;
    $[45] = t24;
    $[46] = t25;
    $[47] = t26;
    $[48] = t27;
    $[49] = tickerBytes;
  } else {
    T0 = $[26];
    T1 = $[27];
    canApplyDerivedTicker = $[28];
    dynamicMaxTickerLength = $[29];
    handleCopyLabelToTicker = $[30];
    t10 = $[31];
    t11 = $[32];
    t12 = $[33];
    t13 = $[34];
    t14 = $[35];
    t15 = $[36];
    t16 = $[37];
    t17 = $[38];
    t18 = $[39];
    t19 = $[40];
    t20 = $[41];
    t21 = $[42];
    t22 = $[43];
    t23 = $[44];
    t24 = $[45];
    t25 = $[46];
    t26 = $[47];
    t27 = $[48];
    tickerBytes = $[49];
  }
  const t28 = tickerBytes > dynamicMaxTickerLength && "text-feedback-error-content";
  let t29;
  if ($[69] !== t28) {
    t29 = cn("text-right tabular-nums", t28);
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== canApplyDerivedTicker || $[72] !== handleCopyLabelToTicker || $[73] !== hasMarket || $[74] !== index || $[75] !== t || $[76] !== ticker || $[77] !== tickerInput || $[78] !== tickerPrefix) {
    t30 = t31 => {
      const {
        controlProps: controlProps_0
      } = t31;
      return <div className={cn("flex h-10 w-full min-w-0 items-center rounded-control border border-border-primary bg-background-primary shadow-none", "transition-[color,background-color,border-color,box-shadow]", "focus-within:ring-2 focus-within:ring-border-primary focus-within:ring-offset-2", "hover:border-border-brand", controlProps_0["aria-invalid"] === true && "border-feedback-error-border focus-within:ring-feedback-error-border")}><span className="flex h-full shrink-0 items-center rounded-l-[calc(var(--radius-control)-1px)] border-r border-border-primary bg-background-tertiary/40 px-3 text-body font-medium tabular-nums text-foreground-primary">{tickerPrefix}</span>{hasMarket && <span aria-hidden="true" className="flex h-full shrink-0 items-center border-r border-border-primary bg-background-tertiary/40 px-2 text-body font-medium tabular-nums text-foreground-tertiary">X</span>}<input {...controlProps_0} type="text" value={ticker} {...tickerInput} placeholder={t("outcomeTickerPlaceholder")} className="min-w-0 flex-1 bg-transparent px-3 py-2 text-body-lg text-foreground-primary outline-none placeholder:text-foreground-tertiary focus-visible:outline-none md:text-body" />{canApplyDerivedTicker && <AppTooltip content={t("outcomeTickerUseLabel")}><button type="button" onClick={handleCopyLabelToTicker} aria-label={t("outcomeTickerUseLabel")} data-testid={`outcome-ticker-use-label-${String(index)}`} className="flex h-full shrink-0 items-center justify-center rounded-r-[calc(var(--radius-control)-1px)] border-l border-border-primary bg-background-tertiary/40 px-3 text-foreground-secondary transition-colors hover:bg-background-tertiary hover:text-foreground-primary focus-visible:outline-none"><Icon icon={Copy01Icon} className="size-4" /></button></AppTooltip>}</div>;
    };
    $[71] = canApplyDerivedTicker;
    $[72] = handleCopyLabelToTicker;
    $[73] = hasMarket;
    $[74] = index;
    $[75] = t;
    $[76] = ticker;
    $[77] = tickerInput;
    $[78] = tickerPrefix;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== T0 || $[81] !== t10 || $[82] !== t11 || $[83] !== t12 || $[84] !== t13 || $[85] !== t14 || $[86] !== t29 || $[87] !== t30) {
    t31 = <T0 id={t10} label={t11} error={t12} labelClassName={t13} description={t14} descriptionClassName={t29}>{t30}</T0>;
    $[80] = T0;
    $[81] = t10;
    $[82] = t11;
    $[83] = t12;
    $[84] = t13;
    $[85] = t14;
    $[86] = t29;
    $[87] = t30;
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  let t32;
  if ($[89] !== t15 || $[90] !== t16 || $[91] !== t31) {
    t32 = <div className={t15}>{t16}{t31}</div>;
    $[89] = t15;
    $[90] = t16;
    $[91] = t31;
    $[92] = t32;
  } else {
    t32 = $[92];
  }
  let t33;
  if ($[93] !== T1 || $[94] !== t17 || $[95] !== t18 || $[96] !== t19 || $[97] !== t20 || $[98] !== t21 || $[99] !== t22 || $[100] !== t23 || $[101] !== t24 || $[102] !== t25 || $[103] !== t26 || $[104] !== t32) {
    t33 = <T1 index={t17} displaySrc={t18} isNsfw={t19} imageRequired={t20} imageError={t21} imageLoading={t22} eagerImage={t23} pinFailed={t24} onOpen={t25} onRemove={t26}>{t32}</T1>;
    $[93] = T1;
    $[94] = t17;
    $[95] = t18;
    $[96] = t19;
    $[97] = t20;
    $[98] = t21;
    $[99] = t22;
    $[100] = t23;
    $[101] = t24;
    $[102] = t25;
    $[103] = t26;
    $[104] = t32;
    $[105] = t33;
  } else {
    t33 = $[105];
  }
  let t34;
  if ($[106] !== footerSlot) {
    t34 = footerSlot !== undefined && footerSlot !== null ? <div className="flex justify-end">{footerSlot}</div> : null;
    $[106] = footerSlot;
    $[107] = t34;
  } else {
    t34 = $[107];
  }
  let t35;
  if ($[108] !== imageModalOpen || $[109] !== label || $[110] !== marketTitle || $[111] !== onImagePending || $[112] !== onImageReject) {
    t35 = <MarketImagePickerModal open={imageModalOpen} onOpenChange={setImageModalOpen} mode="outcome" marketTitle={marketTitle} outcomeLabel={label} onPending={onImagePending} onReject={onImageReject} />;
    $[108] = imageModalOpen;
    $[109] = label;
    $[110] = marketTitle;
    $[111] = onImagePending;
    $[112] = onImageReject;
    $[113] = t35;
  } else {
    t35 = $[113];
  }
  let t36;
  if ($[114] !== t27 || $[115] !== t33 || $[116] !== t34 || $[117] !== t35) {
    t36 = <div className={t27}>{t33}{t34}{t35}</div>;
    $[114] = t27;
    $[115] = t33;
    $[116] = t34;
    $[117] = t35;
    $[118] = t36;
  } else {
    t36 = $[118];
  }
  return t36;
}